import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { css } from '@emotion/css';
import { Button } from './components/styles/Button.styled';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import type { Address, Utilities } from './types';
import { fetchAddress, fetchUtility } from './api/api';
import { Dropdown } from 'react-bootstrap';
import { ChooseDataSourceModal } from './components/choose-data-source-modal';
import { trackEvent } from './analytics';

const MIN_ADDRESS_SEARCH_LENGTH = 2;
const ADDRESS_SEARCH_DELAY = 350;

export const ValidateAddress = () => {
    const { t } = useTranslation('homepage');
    const navigate = useNavigate();
    const [ loading, setLoading ] = useState(false);
    const [ addressOptions, setAddressOptions ] = useState<Address[]>([]);
    const [ selectedAddress, setSelectedAddress ] = useState<Address[]>([]);
    const [ showDataSourceModal, setShowDataSourceModal ] = useState(false);
    const [ cookies, , removeCookie ] = useCookies(['consent_id']);
    const [ loadingUtility, setLoadingUtility ] = useState(false);
    const [ utility, setUtility ] = useState<Utilities[]>([]);
    const [ selectedUtility, setSelectedUtility ] = useState<Utilities | undefined>(undefined);
    const searchRequestId = useRef(0);

    useEffect(() => {
        const savedAddress = localStorage.getItem('saved_address');
        if (savedAddress) {
            fetchAddress(savedAddress).then(data => {
                if (data && data.length > 0) {
                    setSelectedAddress([data[0]]);
                }
            })
        }
    }, []);

    useEffect(() => {
        if (selectedAddress.length === 0) return;
        setLoadingUtility(true);
        const { zipcode } = selectedAddress[0];
        fetchUtility(zipcode).then(res => {
            setLoadingUtility(false);
            if (res) {
                setUtility(res);
                setSelectedUtility(res[0]);
            }
        })
    }, [selectedAddress])

    const handleSearch = useCallback(async (val:string) => {
        const searchTerm = val.trim();
        const requestId = ++searchRequestId.current;

        if (searchTerm.length < MIN_ADDRESS_SEARCH_LENGTH) {
            setAddressOptions([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const data = await fetchAddress(searchTerm);
            if (requestId === searchRequestId.current) {
                setAddressOptions(data);
            }
        } finally {
            if (requestId === searchRequestId.current) {
                setLoading(false);
            }
        }
    }, []);

    const handleInputChange = useCallback((val: string) => {
        searchRequestId.current += 1;
        setAddressOptions([]);

        if (val.trim().length < MIN_ADDRESS_SEARCH_LENGTH) {
            setLoading(false);
        }
    }, []);

	const handleClick = () => {
        if (selectedAddress.length === 0) return;
        const address = selectedAddress[0];
        localStorage.setItem('saved_address', address.street);
        redirectTo('plan');
	}

    const redirectTo = (type: string) => {
        const address = selectedAddress[0];
        const { street, esiid, zipcode, city, state } = address;
        const utilityCode = selectedUtility ? selectedUtility.utility_code : '';
        trackEvent('search_address', {
            utility_code: utilityCode,
            destination: type === 'plan' ? 'plans' : 'get_usage',
            source: type,
        });
        if (type === 'plan') {
            if (cookies) {
                removeCookie('consent_id');
            }
            navigate(`/plans?address=${encodeURIComponent(street)}&esiid=${esiid}&zipcode=${zipcode}&city=${city}&state=${state}&utcode=${utilityCode}`);
        }
        else {
            navigate(`/get_usage?address=${encodeURIComponent(street)}&esiid=${esiid}&zipcode=${zipcode}&city=${city}&state=${state}&source=${type}&utcode=${utilityCode}`);
        }
    }

    const chooseUtility = () => {
        if (utility.length <= 1 || !selectedUtility) return null;
        return (
            <Dropdown className="utility-select">
                <Dropdown.Toggle variant="light">
                    {selectedUtility.utility_name}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {
                        utility.map((val, idx) => (
                            <Dropdown.Item
                                eventKey={idx}
                                key={idx}
                                onClick={() => {
                                    setSelectedUtility(val);
                                }}
                                active={val.utility_id === selectedUtility.utility_id}
                            >{val.utility_name}</Dropdown.Item>
                        ))
                    }
                </Dropdown.Menu>
            </Dropdown>
        )
    }

    return (
        <div>
            <div className="autocomplete-address-row">
                <div id="autocomplete-wrapper">
                    <AsyncTypeahead
                        filterBy={() => true}
                        id="autocomplete"
                        isLoading={loading}
                        labelKey="label"
                        minLength={MIN_ADDRESS_SEARCH_LENGTH}
                        delay={ADDRESS_SEARCH_DELAY}
                        onSearch={handleSearch}
                        onInputChange={handleInputChange}
                        options={addressOptions}
                        placeholder={`${t('Address')}...`}
                        size="lg"
                        emptyLabel={loading ? null : undefined}
                        onChange={(selected) => setSelectedAddress(selected as Address[])}
                        selected={selectedAddress}
                    />
                </div>
                { chooseUtility() }
            </div>
            <Button
                onClick={handleClick}
                disabled={!selectedAddress[0] || loadingUtility}
                className={css`
                    margin-top: 20px;
                    @media (max-width: 768px) {
                        width: 100%;
                    }
                `}
            >
                <span>{t('Get Started')}</span>
            </Button>
            <ChooseDataSourceModal
                show={showDataSourceModal}
                handleClose={() => {setShowDataSourceModal(false);}}
                handleRedirect={redirectTo}
                utility={selectedUtility}
            />
        </div>
    )
}